import type {
  WavesGate,
  WavesObstacle,
  WavesSaw,
  WavesTerrain,
} from '@shared/wavesChart';
import { ceilingYAt, floorYAt, WAVES_PERFECT_CENTER_BAND_FRAC } from '@shared/wavesChart';
import type { WavesLayout } from '../wavesPlayfieldGeometry';
import {
  obstacleCanvasX,
  OBSTACLE_BEATS_VISIBLE_AHEAD,
  playfieldYToCanvas,
} from '../wavesPlayfieldGeometry';
import {
  OBSTACLE_GLOW_THICKNESS,
  OBSTACLE_VISUAL_THICKNESS,
  SAW_HUB_FRAC,
  SAW_SPIN_RAD_PER_BEAT,
  SAW_TEETH,
  SAW_TOOTH_RAKE_FRAC,
  SAW_VALLEY_FRAC,
} from './wavesRenderConstants';

/**
 * Paint a single Waves obstacle at its scroll position for `beat`.
 * Gates draw as vertical pillars with a gap; saws draw as a circular
 * hazard with a hatched centre. Gate pillars stop at the LOCAL terrain
 * surface (sampled at the obstacle's beat) so a spike sits flush on the
 * winding corridor wall rather than overshooting into the rock fill.
 */
export function drawWavesObstacle(
  ctx: CanvasRenderingContext2D,
  obs: WavesObstacle,
  layout: WavesLayout,
  terrain: WavesTerrain,
  beat: number,
  playerRadius: number,
  hazardColour: string,
  approachPulse: number,
  chromeShadows: boolean,
): void {
  const x = obstacleCanvasX(layout, beat, obs.beat);
  if (obs.kind === 'gate') {
    const topY = playfieldYToCanvas(layout, ceilingYAt(terrain, obs.beat));
    const bottomY = playfieldYToCanvas(layout, floorYAt(terrain, obs.beat));
    const beatsAhead = obs.beat - beat;
    const showPerfectBand =
      beatsAhead > 0 && beatsAhead <= OBSTACLE_BEATS_VISIBLE_AHEAD;
    drawGate(
      ctx,
      obs,
      x,
      topY,
      bottomY,
      layout,
      playerRadius,
      hazardColour,
      approachPulse,
      chromeShadows,
      showPerfectBand,
    );
  } else {
    drawSaw(ctx, obs, x, beat, layout, hazardColour, approachPulse, chromeShadows);
  }
}

function drawGate(
  ctx: CanvasRenderingContext2D,
  obs: WavesGate,
  x: number,
  corridorTop: number,
  corridorBottom: number,
  layout: WavesLayout,
  playerRadius: number,
  hazardColour: string,
  approachPulse: number,
  chromeShadows: boolean,
  showPerfectBand: boolean,
): void {
  const gapTopCanvas = playfieldYToCanvas(layout, obs.gapTopY);
  const gapBottomCanvas = playfieldYToCanvas(layout, obs.gapBottomY);
  const halfWidth = OBSTACLE_VISUAL_THICKNESS / 2;
  ctx.fillStyle = hazardColour;
  if (chromeShadows) {
    ctx.shadowColor = hazardColour;
    ctx.shadowBlur = OBSTACLE_GLOW_THICKNESS * 2 + approachPulse * 16;
  }
  if (gapTopCanvas > corridorTop) {
    ctx.fillRect(x - halfWidth, corridorTop, OBSTACLE_VISUAL_THICKNESS, gapTopCanvas - corridorTop);
  }
  if (gapBottomCanvas < corridorBottom) {
    ctx.fillRect(
      x - halfWidth,
      gapBottomCanvas,
      OBSTACLE_VISUAL_THICKNESS,
      corridorBottom - gapBottomCanvas,
    );
  }
  ctx.shadowBlur = 0;
  // Cap each lethal pillar with a point at its gap-facing end so the
  // spike reads on the surface that actually kills: a ceiling spike's
  // pillar hangs from the top and points DOWN; a floor spike's pillar
  // rises from the bottom and points UP. Tunnel gates have a pillar at
  // both ends, so they take both tips.
  if (obs.visual === 'spike-ceiling' || obs.visual === 'pillar') {
    drawSpikeTip(ctx, x, gapTopCanvas, halfWidth, OBSTACLE_VISUAL_THICKNESS, 'down', hazardColour);
  }
  if (obs.visual === 'spike-floor' || obs.visual === 'pillar') {
    drawSpikeTip(ctx, x, gapBottomCanvas, halfWidth, OBSTACLE_VISUAL_THICKNESS, 'up', hazardColour);
  }
  if (showPerfectBand) {
    drawPerfectBandHint(
      ctx,
      x,
      gapTopCanvas,
      gapBottomCanvas,
      layout,
      playerRadius,
      halfWidth,
    );
  }
}

function drawPerfectBandHint(
  ctx: CanvasRenderingContext2D,
  x: number,
  gapTopCanvas: number,
  gapBottomCanvas: number,
  layout: WavesLayout,
  playerRadius: number,
  halfWidth: number,
): void {
  const gapHeight = gapBottomCanvas - gapTopCanvas;
  const playable = gapHeight - 2 * playerRadius * layout.scaleY;
  if (playable <= 4) {
    return;
  }
  const bandHeight = playable * WAVES_PERFECT_CENTER_BAND_FRAC;
  const centerY = (gapTopCanvas + gapBottomCanvas) / 2;
  ctx.save();
  ctx.fillStyle = 'rgba(59, 255, 157, 0.22)';
  ctx.fillRect(
    x - halfWidth,
    centerY - bandHeight / 2,
    OBSTACLE_VISUAL_THICKNESS,
    bandHeight,
  );
  ctx.strokeStyle = 'rgba(59, 255, 157, 0.55)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - halfWidth - 4, centerY);
  ctx.lineTo(x + halfWidth + 4, centerY);
  ctx.stroke();
  ctx.restore();
}

function drawSpikeTip(
  ctx: CanvasRenderingContext2D,
  x: number,
  baseY: number,
  halfWidth: number,
  thickness: number,
  direction: 'up' | 'down',
  hazardColour: string,
): void {
  ctx.fillStyle = hazardColour;
  const tipHeight = thickness;
  const tipY = direction === 'down' ? baseY + tipHeight : baseY - tipHeight;
  ctx.beginPath();
  ctx.moveTo(x - halfWidth, baseY);
  ctx.lineTo(x + halfWidth, baseY);
  ctx.lineTo(x, tipY);
  ctx.closePath();
  ctx.fill();
}

/**
 * A rotating circular saw blade: sharp forward-raked teeth around a dark
 * hub. The spin is beat-locked (deterministic, ties to the song clock)
 * with a per-blade phase from `centerY` so neighbouring saws don't lock
 * in lockstep. The tooth-tip radius matches the obstacle's collision
 * radius, so the menacing silhouette reads as the actual danger zone.
 */
function drawSaw(
  ctx: CanvasRenderingContext2D,
  obs: WavesSaw,
  x: number,
  beat: number,
  layout: WavesLayout,
  hazardColour: string,
  approachPulse: number,
  chromeShadows: boolean,
): void {
  const y = playfieldYToCanvas(layout, obs.centerY);
  const tipR = obs.radius * layout.scaleX * (1 + approachPulse * 0.08);
  const valleyR = tipR * SAW_VALLEY_FRAC;
  const angle = beat * SAW_SPIN_RAD_PER_BEAT + obs.centerY * 0.05;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  ctx.fillStyle = hazardColour;
  if (chromeShadows) {
    ctx.shadowColor = hazardColour;
    ctx.shadowBlur = 12 + approachPulse * 18;
  }
  ctx.beginPath();
  for (let i = 0; i < SAW_TEETH; i += 1) {
    const a0 = (i / SAW_TEETH) * Math.PI * 2;
    const a1 = ((i + 1) / SAW_TEETH) * Math.PI * 2;
    const tipA = a0 + (a1 - a0) * SAW_TOOTH_RAKE_FRAC;
    if (i === 0) {
      ctx.moveTo(Math.cos(a0) * valleyR, Math.sin(a0) * valleyR);
    } else {
      ctx.lineTo(Math.cos(a0) * valleyR, Math.sin(a0) * valleyR);
    }
    ctx.lineTo(Math.cos(tipA) * tipR, Math.sin(tipA) * tipR);
    ctx.lineTo(Math.cos(a1) * valleyR, Math.sin(a1) * valleyR);
  }
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;

  // Dark hub with a bright bolt so the blade reads as machinery, not a
  // flat disc, and the rotation is legible.
  ctx.fillStyle = '#0a0a0a';
  ctx.beginPath();
  ctx.arc(0, 0, tipR * SAW_HUB_FRAC, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = hazardColour;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-tipR * SAW_HUB_FRAC * 0.7, 0);
  ctx.lineTo(tipR * SAW_HUB_FRAC * 0.7, 0);
  ctx.stroke();

  ctx.restore();
}
